<template>
  <div class="txn-details-page">
    <slot name='title' />
    <div>
      <!-- Plain in-page action bar (master shape) — native draws the app header/back;
           this row only carries the web-owned Cancel/Done actions for the reconcile form. -->
      <div class="navless-layout-header">
        <slot name='navbar'>
          <!-- Plain anchor, like every other bar's Back (those use ed-button WITH :to,
               whose template branch renders a bare <a>). ed-button withOUT :to falls
               into its <button class="btn btn-primary"> branch — an oversized
               primary-05 box clashing with the bar's primary-04 strip. goBack must
               stay a click handler (cancel cleanup), so :to isn't an option here. -->
          <a href="javascript:;" @click="goBack">
            <i class="fal fa-chevron-left"></i>
            &nbsp;
            {{ cannotBeReconciled || transactionComplete ? 'Back' : 'Cancel' }}
          </a>
          <!-- `submit-btn` only — the bar is now a flex row with
               justify-content: space-between, which right-aligns this second child;
               the old `pull-right` float is ignored inside a flex container, and
               `form-group`'s bottom margin would shift it off the bar's vertical
               center (Cancel/Done then sit on different y-axes). -->
          <a
            v-if="!cannotBeReconciled && (isUserAdmin || reconcileAction !== 'view')"
            href="javascript:;"
            class="submit-btn"
            :class="{ 'submit-btn--saving': isSaving || !uploadFinished }"
            @click='confirm'
          >
            <strong>Done</strong>
          </a>
        </slot>
      </div>
      <br />

      <!-- Not allowed -->
      <div v-if='cannotBeReconciled'>
        <br />
        <ed-notice type='danger' icon='fas fa-exclamation-circle'>
          Transaction Declined: {{ transaction.data.DeclineReason }}
        </ed-notice>
      </div>

      <div class="card" v-if="transaction.data.Id">
        <ed-notice class='card'>
          <div class='details'>
            <div class="row">
              <div class="col-xs-9">
                <p class="overflow-merchant"><strong>{{ transaction.data.merchant }}</strong></p>
                <p><small>{{ moment(transaction.data.date).format('YYYY-MM-DD') }}</small></p>
              </div>
              <div class="col-xs-3">
                <p style="text-align:right"><strong><v-money>{{ transaction.data.amount }}</v-money></strong></p>
              </div>
            </div>
          </div>
        </ed-notice>
      </div>

      <!-- Complete -->
      <div v-if='transactionComplete'>
        <br />
        <ed-notice type='alt' icon='fas fa-check-circle'>
          This transaction was submitted in envelope {{ transaction.data.EnvelopeNumber }}
        </ed-notice>
      </div>

      <!-- Transaction chip per Figma 538:788 — yellow left stripe, blue 1px border, white bg -->
      <div class="txn-chip">
        <span class="txn-chip__stripe"></span>
        <div class="txn-chip__body">
          <div class="txn-chip__top">
            <span class="txn-chip__merchant">{{ transaction.data.merchant }}</span>
            <span class="txn-chip__amount"><v-money>{{ transaction.data.amount }}</v-money></span>
          </div>
          <div class="txn-chip__date">
            {{ formatChipDate(transaction.data.date) }}
          </div>
        </div>
      </div>

      <!-- Not allowed -->
      <div v-if="cannotBeReconciled">
        <p>
          <strong>Card Holder</strong>
          <br />
          <small style="margin-left:10px;">{{ transaction.data.Holder }}</small>
        </p>
        <p>
          <strong>Card Number</strong>
          <br />
          <small style="margin-left:10px;">{{ transaction.data.CardNumber }}</small>
        </p>
        <p>
          <strong>{{ divisionName }}</strong>
          <br />
          <small style="margin-left:10px;">{{ transaction.data.Division }}</small>
        </p>
      </div>

      <div v-if='!cannotBeReconciled'>
        <Reconcile
          class="reconcile-form"
          v-if="transaction.data.DivisionId"
          :transaction="transaction"
          :fields-incomplete="{ receiptIncomplete, descriptionIncomplete }"
          :wrong-split-total="wrongSplitTotal"
          :reconcile-action="reconcileAction"
          :receipt-edit-allowed="receiptEditAllowed"
          :is-mobile="isMobile"
          @reconcile-created="handleReconcileCreated"
          @fields-updated="handleFieldsUpdated"
          @mediaFilesPathUpdated="updateMediaFilesPath"
          @attachmentsUpdated="updateAttachmentsFiles"
          @additional-documents-updated="updateAdditionalDocuments"
          @split-amounts="handleSplitAmounts"
          @description-text="handleDescriptionText"
          @uploadFinished="handleUploadFinished"
          ref="reconcileComp"
        />
      </div>
      <br /><br />
    </div>
  </div>
</template>

<script>

import Reconcile from '@/components/_modules/Programs/Card-transactions/ReconcileTransaction'
import CardTransactions from '@/components/_modules/Programs/Card-transactions'
import { singularize } from '@/core/utils/string.js'
import { EventBus } from '@/event-bus'
const transactions = []

const { methods: transactionsMethods } = CardTransactions
const { methods: reconcileMethods } = Reconcile

export default {
  alias: 'evedpaytransactiondetails',
  components: {
    Reconcile
  },
  created() {
    // Mobile EvedPay surface vs the shared desktop module — gates the mobile-only
    // Reconcile UI (full-screen scan overlay, mobile Additional Documents layout)
    // via the :is-mobile prop. Same detection as ReceiptFolder.vue / CandidateSelector.
    this.isMobile = global.config && global.config.layout === 'mobile'
    this.divisionName = singularize(this.$root.getUser.sessionSettings.CompanySettings.Labels.DivisionName)
    this.hasBu = this.$root.getUser.settings.HasBusinessUnit
    this.hasCc = this.$root.getUser.settings.HasCostCenter
    this.hasBuAndCcPair = this.$root.getUser.settings.UseBusinessUnitAndCostCenterMapping

    const Id = this.$route.query.transactionid

    this.api.forms.post(this.api.$.evedpaycard.transactions, {
      sort: 'TransactionStatus',
      dir: 'ASC',
      start: 0,
      limit: 1000,
      isLiveGrid: true,
      filter: JSON.stringify({ 'Id': Id, searchTerm: '' })
    }).then(r => {
      this.transactions = r.data.Data.map(transaction => ({
        ...transaction,
        merchant: transaction.Merchant,
        amount: transaction.Amount,
        date: transaction.TransactionDate,
        status: transaction.TransactionStatus,
        reconcileStatus: transaction.ReconciliationStatus,
        id: transaction.Id
      }))

      this.transaction.data = this.transactions[0] || {}

      this.selectedProgram = {
        Id: this.transaction.data.ProgramId,
        Name: this.transaction.data.ProgramName
      }

      this.handleTransaction(this.transaction)

      this.transaction.data.ProgramId && this.getCategoriesAndBuAndCc(this.transaction.data.ProgramId)
    })
  },
  data() {
    return {
      transactions,
      isSaving: false,
      // SPRT-18752 — false while a receipt/file upload is in flight (driven by the
      // ReconcileTransaction `uploadFinished` event, set via handleUploadFinished from
      // the spread-in Card-transactions methods). Gates the Done button so it can't be
      // tapped mid-upload — mirrors the desktop Save button's :primaryButtonDisabled="!uploadFinished".
      uploadFinished: true,
      isMobile: false,
      hasBu: false,
      hasCc: false,
      hasBuAndCcPair: false,
      programs: [],
      programOptions: [],
      businessUnits: [],
      costCenters: [],
      buAndCcPairs: [],
      categories: [],
      supplierIncomplete: false,
      programIncomplete: false,
      requireReceiptDescription: Boolean,
      descriptionIncomplete: false,
      receiptIncomplete: false,
      buAndCcPairIncomplete: [],
      buIncomplete: [],
      ccIncomplete: [],
      categoryIncomplete: [],
      form: [{}],
      selectedProgram: {},
      selectedSupplier: {},
      selectedCategory: [],
      selectedBu: [],
      selectedCc: [],
      selectedBuAndCcPair: [],
      itemIds: [],
      referenceId: [],
      filteredAttachments: [],
      // PRODUCTDEV-5772 — supporting documents, kept separate from filteredAttachments
      // (receipts) so they never feed IsComplete / ReceiptFiles / Reconciliation Status.
      additionalDocumentsFiles: [],
      wrongSplitTotal: false,
      noteText: '',
      selectedCardId: null,
      selectedTransactionId: null,
      hasErrors: false,
      reconcileAction: 'create',
      divisionName: '',
      transaction: { data: {} },
      waitingApprovalStatuses: ['Open - Waiting Approval', 'Exported - Waiting Approval', 'Posted - Waiting Approval'],
      approvalUsersData: [],
      transactionInEnvelopeStatuses: ['In Envelope', 'Exported', 'Posted'],
      editAuth: null
    }
  },
  computed: {
    cannotBeReconciled() {
      return this.transaction.data.status === 'Declined'
    },
    transactionComplete() {
      return this.transactionInEnvelopeStatuses.includes(this.transaction.data.status) && (this.transaction.data.reconcileStatus === 'Approved' || this.transaction.data.reconcileStatus === 'Coded')
    },
    moment() { return global.moment },
    programName() {
      const label = this.$root.getUser.settings.Labels.ProgramLabel
      return label ? this.singularize(label) : label
    },
    isUserAdmin() {
      if (this.$user.$.userPermissions.includes('EvedPayCardAdministrator')) {
        return true
      }
      return false
    },
    // PRODUCTDEV-5574 — preserves the pre-existing receipt-upload rules. See the matching computed in
    // Card-transactions/index.vue for the rationale.
    receiptEditAllowed() {
      if (this.isUserAdmin) return true
      const t = this.transaction && this.transaction.data
      if (!t || !t.TransactionStatus) return false
      const inEnvelopeReadonly = ['In Envelope', 'Exported', 'Posted'].includes(t.TransactionStatus)
      if (!inEnvelopeReadonly) return t.ReconciliationStatus !== 'Approved'
      if (t.EnvelopeStatus === 'Draft') return true
      if (t.ReconciliationStatus === 'Approved') return false
      return this.isUserInApprovalList()
    }
  },
  methods: {
    ...reconcileMethods,
    ...transactionsMethods,
    async confirm() {
      // In-flight guard: Done is a plain <a> (no native disabled state) and this
      // mobile path calls createReconcile directly — the desktop savePressed gate
      // (saveAndReconcile, Card-transactions/index.vue) never runs here. Without
      // the guard, a double-tap on a slow connection fires two reconcile POSTs
      // for the same transaction.
      // SPRT-18752 — also block save while a receipt/file upload is still in flight
      // (uploadFinished is false). The disabled class already sets pointer-events:none;
      // this guards against any CSS-specificity gap, same as the isSaving guard.
      if (this.isSaving || !this.uploadFinished) {
        return
      }
      this.isSaving = true
      this.hasErrors = false
      try {
        await transactionsMethods.createReconcile.call(this)
        // PRODUCTDEV-5783 — on a successful save, claim any receipt picked from the folder
        // this session (deferred FolderSaved → Matched) so it leaves the folder only on save.
        if (!this.hasErrors && this.$refs.reconcileComp &&
            typeof this.$refs.reconcileComp.claimPendingFolderReceipts === 'function') {
          this.$refs.reconcileComp.claimPendingFolderReceipts()
        }
        if (this.hasErrors) {
          return
        }
        // Only announce a reconcile once the save actually succeeded — listeners
        // (cardtransactions/transactions/Card-* grids) refetch on this event, so
        // firing it before the hasErrors guard caused a needless refetch + UI churn
        // on a FAILED save.
        EventBus.$emit('transaction-reconciled', this.$route.query.transactionid)
        // Prune the receipt-flow segment (submitreceipt + this details page) from the
        // back-button history BEFORE navigating — pushing the landing page on top of
        // it buried those entries, and the landing page's Back then pulled the user
        // straight back into the already-saved flow (same loop goBack had,
        // 2026-06-05). popReceiptFlowHistory also hands back the genuine origin
        // fullPath so the same-destination cases can restore it token-and-all.
        const origin = this.popReceiptFlowHistory()
        const originPath = origin ? origin.split('?')[0] : null

        // Receipt-capture (FAB / folder) flow: the user started from the camera or receipt folder,
        // not from a specific card, so after a successful reconcile send them to the Cards tab
        // rather than drilling into one card's list. SubmitReceipt threads a receiptScanId
        // through the query, which marks this flow.
        if (this.$route.query.receiptScanId) {
          if (originPath === '/evedpay/cards') {
            // The origin WAS the Cards tab — reuse its recorded fullPath (token intact).
            this.$router.push({ path: origin })
            return
          }
          const cardsQuery = Object.assign({}, this.$route.query)
          delete cardsQuery.transactionid
          delete cardsQuery.receiptScanId
          delete cardsQuery.cardid
          delete cardsQuery.cardNumber
          this.$router.push({ path: '/evedpay/cards', query: cardsQuery })
          return
        }
        // Normal reconcile (opened from a specific card): land on that card's transaction list so the
        // user sees the transaction grouped under "Completed". NOT $router.go(-1): that pops back into
        // /evedpay/submitreceipt and re-triggers the receipt-capture flow. Drop transactionid +
        // receiptScanId from the query so the next page reads fresh and the same scan can't
        // accidentally be re-matched.
        const cardId = (this.transaction.data && this.transaction.data.CardId) || this.$route.query.cardid || this.selectedCardId
        if (originPath === '/evedpay/cardtransactions' && (!cardId || origin.indexOf(String(cardId)) !== -1)) {
          // Same card list the user came from — restore its recorded fullPath
          // (token + cardNumber intact) instead of pushing a thinner duplicate.
          this.$router.push({ path: origin })
          return
        }
        const query = Object.assign({}, this.$route.query)
        delete query.transactionid
        delete query.receiptScanId
        if (cardId) query.cardid = cardId
        this.$router.push({ path: '/evedpay/cardtransactions', query })
      } finally {
        // Module is kept-alive — reset so the next entry (or a retry after a
        // validation/save error) can save again.
        this.isSaving = false
      }
    },
    stopSave() {
      this.hasErrors = true
    },
    // PRODUCTDEV-5772 — supporting documents emitted by ReconcileTransaction, kept
    // separate from filteredAttachments (receipts) and posted as AdditionalDocuments;
    // never part of ReceiptFiles / IsComplete / Reconciliation Status.
    updateAdditionalDocuments(additionalDocuments) {
      this.additionalDocumentsFiles = additionalDocuments
    },
    // Figma 538:788 chip date — "Sep 27 2023" style
    formatChipDate(value) {
      if (!value) return ''
      var m = global.moment(value)
      return m.isValid() ? m.format('MMM DD YYYY') : ''
    },
    async handleTransaction(transaction) {
      // Reset auth state up-front so a fetch failure or skipped fetch cannot leak the previous
      // transaction's permissions into the current one.
      this.editAuth = null
      if (transaction && transaction.data) {
        this.selectedCardId = transaction.data.CardId
        this.selectedTransactionId = transaction.data.Id
        if (transaction.data.EnvelopeId && transaction.data.EnvelopeId !== '00000000-0000-0000-0000-000000000000') {
          await this.fetchEditAuth(transaction.data.Id)
        }
        if (this.waitingApprovalStatuses.includes(transaction.data.EnvelopeStatus)) {
          await this.getApprovalUsers(transaction.data.EnvelopeId)
        }
        await this.handleReconcileAction(transaction)
      } else {
        this.reconcileAction = 'create'
      }
    },
    async handleReconcileAction(transaction) {
      const isInEnvelopeByTxStatus = this.transactionInEnvelopeStatuses.includes(transaction.data.TransactionStatus)
      if (transaction.data.ReconciliationStatus !== 'Open') {
        if (transaction.data.ReconciliationStatus === 'Coded') {
          if (transaction.data.EnvelopeStatus === 'Draft') {
            this.reconcileAction = 'edit'
          } else if (!isInEnvelopeByTxStatus || this.canEditInEnvelopeFromReconcileModal()) {
            this.reconcileAction = 'edit'
          } else {
            this.reconcileAction = 'view'
          }
        } else if (isInEnvelopeByTxStatus && this.canEditInEnvelopeFromReconcileModal()) {
          // PRODUCTDEV-5574 — Approved envelopes (e.g. Open - Approved) remain editable from the
          // Reconcile modal for authorized roles; per spec they are not editable from the envelope page.
          this.reconcileAction = 'edit'
        } else {
          this.reconcileAction = 'view'
        }
      } else if (isInEnvelopeByTxStatus) {
        if (transaction.data.EnvelopeStatus === 'Draft') {
          this.reconcileAction = 'edit'
        } else if (this.canEditInEnvelopeFromReconcileModal()) {
          this.reconcileAction = 'edit'
        } else {
          this.reconcileAction = 'view'
        }
      } else {
        this.reconcileAction = 'create'
      }
    },
    async fetchEditAuth(transactionId) {
      try {
        const response = await this.api.get(this.api.$.evedpaycard.canEditInEnvelopeTransaction, {
          params: { transactionId }
        })
        this.editAuth = response.data || null
      } catch (error) {
        console.error('There was a problem fetching transaction edit authorization:', error)
        // Fail closed to view-only, but tell the user why so a transient can-edit
        // failure doesn't read as a silent permission downgrade.
        this.editAuth = null
        this.$notifications.notify({ type: 'plain', icon: 'exclamation-circle', placement: { from: 'top', align: 'center' }, timeout: 0, message: 'We could not verify your edit permissions for this transaction. Please refresh and try again.', discard: 'Ok', goto: '' })
      }
    },
    async getApprovalUsers(envelopeId) {
      await this.api.get(this.api.$.evedpaycard.approvalUsersForEnvelope, {
        params: {
          envelopeId
        }
      })
        .then(response => {
          this.approvalUsersData = response.data
        })
        .catch(error => {
          console.error('There was a problem with the approval users request:', error)
        })
    },
    // PRODUCTDEV-5574 — the Reconcile modal does not grant edit access to a Declined-envelope submitter
    // who isn't also a card holder/admin/approver/global admin. That submitter-only path is the
    // envelope page's responsibility (CreateEnvelope.vue editEnvelope flow).
    canEditInEnvelopeFromReconcileModal() {
      // ASP.NET MVC's default JsonResult serializes property names as-is (PascalCase),
      // matching the convention used by other endpoints in this controller.
      if (!this.editAuth || !this.editAuth.CanEdit) return false
      return this.editAuth.IsCardHolder ||
             this.editAuth.IsCardAdmin ||
             this.editAuth.IsApprover ||
             this.editAuth.IsGlobalAdmin
    },
    // Retained for `receiptEditAllowed`. Receipt-upload follows its OWN pre-existing rules
    // (current-approver-only when in envelope) and must NOT widen with the new edit-auth.
    isUserInApprovalList() {
      const currentUserID = this.$root.getUser.$.user.UserID
      if (Array.isArray(this.approvalUsersData)) {
        return this.approvalUsersData.some(user => user.Id === currentUserID && user.IsCurrentAction)
      }
      return false
    },
    goBack() {
      // Cancel cleanup: release any in-session uploaded scans back to the user's folder
      // so they aren't lost (they were auto-linked to this transaction on upload — without
      // release-to-folder they'd stay Matched, invisible to the folder picker, and
      // unrecoverable). Mirrors Card-transactions/index.vue's closeReconcile cleanup gate.
      // confirm() handles the save path and navigates directly via $router.push, so we
      // never get here after a successful Done — only on Cancel/Back.
      if (this.$refs.reconcileComp && typeof this.$refs.reconcileComp.cleanupUnsavedReceipts === 'function') {
        this.$refs.reconcileComp.cleanupUnsavedReceipts()
      }
      // Module is kept-alive — clear supporting documents so the next entry starts clean.
      this.additionalDocumentsFiles = []

      // Rewind PAST the whole receipt-capture detour instead of pushing a synthetic
      // per-card entry on top of it. The old push-on-top approach buried the
      // submitreceipt + transactiondetails entries underneath the landing page, so
      // the landing page's Back (= $routerHistory.previous()) pulled the user
      // straight back into the canceled flow — Cancel → card list → Back → stale
      // scan pages → Cancel → … (loop reported 2026-06-05). popReceiptFlowHistory
      // prunes that segment and returns the genuine origin fullPath (token and
      // cardNumber intact — $router.push({ path }) re-parses the query out of the
      // string via vue-router normalizeLocation).
      var target = this.popReceiptFlowHistory()
      if (target) {
        this.$router.push({ path: target })
      } else {
        // No usable origin recorded (deep-link straight into details, or a desktop
        // module route underneath) — fall back to the per-card list. selectedCardId
        // backstops the SubmitReceipt-pushed URLs, which carry no cardid: without
        // it a not-yet-loaded transaction produced a query-less /cardtransactions
        // entry (seen as bare '/evedpay/cardtransactions' in the reported stack).
        var cardId = (this.transaction.data && this.transaction.data.CardId) || this.$route.query.cardid || this.selectedCardId
        var query = Object.assign({}, this.$route.query)
        delete query.transactionid
        delete query.receiptScanId
        if (cardId) query.cardid = cardId
        this.$router.push({ path: '/evedpay/cardtransactions', query: query })
      }
    },

    // Strip EVERY receipt-flow entry — submitreceipt / receiptfolder /
    // transactiondetails, including the CURRENT page, which always sits on top —
    // from the vue-router-back-button stack (sessionStorage fullPaths), and return
    // the most recent genuine /evedpay/ fullPath (or null when none exists; the
    // flow entries are pruned either way so a later Back can't resurface them).
    //
    // Filtering the WHOLE stack, not just the trailing segment, matters twice over:
    // these pages are never legitimate Back targets (Cancel/Done deliberately route
    // around them), and sessions polluted by the pre-fix push-on-top behavior carry
    // flow entries BURIED under synthetic card-list pushes — a trailing-only walk
    // would leave those reachable. The plugin's own visitedRecently pop only
    // matches EXACT fullPaths within the last FOUR entries, which is why the old
    // computed pushes (thinner query — token/cardNumber lost) never collapsed the
    // stack on their own (loop reported 2026-06-05).
    popReceiptFlowHistory () {
      var FLOW = /\/evedpay\/(submitreceipt|receiptfolder|transactiondetails)/
      var rh = this.$routerHistory
      if (!rh || typeof rh.get !== 'function' || typeof rh.save !== 'function') {
        return null
      }
      var hist = rh.get() || []
      var kept = []
      for (var k = 0; k < hist.length; k++) {
        // Drop flow entries + adjacent exact duplicates the filtering can expose.
        if (!FLOW.test(hist[k]) && kept[kept.length - 1] !== hist[k]) {
          kept.push(hist[k])
        }
      }
      var last = kept.length ? kept[kept.length - 1] : null
      if (!last || last.indexOf('/evedpay/') === -1) {
        // No EvedPay origin (deep-link straight into details, or only desktop
        // module routes from the login bounce underneath) — keep what's left and
        // let the caller compute a mobile fallback destination.
        rh.save(kept)
        return null
      }
      // Drop the target too — writeHistory re-records it when the push lands.
      kept.pop()
      rh.save(kept)
      return last
    }
  }
}
</script>

<style lang='scss' scoped>
@import "~@/assets/sass/_variables.scss";

/* Native draws the app header and tab bar — no fixed web chrome here. Bottom
 * padding gives the Coding `+ Split / Copy / Paste / Remove` action bar at the
 * end of the form breathing room. */
.txn-details-page {
  padding-bottom: 70px;
}

/* Same row layout as the cardtransactions Back bar (purple bg/colors come from
 * the mobile layout's global .navless-layout-header theme styles) — Cancel sits
 * left, Done sits right via space-between. */
.navless-layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.submit-btn {
  padding-left: 20px;
  &.loading:before {
    left: 5px !important;
    right: auto !important;
    color: #fff !important;
  }
}

/* Done while a save is in flight — plain <a>, so "disabled" is class-driven. */
.submit-btn--saving {
  pointer-events: none;
  opacity: 0.5;
}

/* Figma 538:788 — transaction chip below the centered header */
.txn-chip {
  position: relative;
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #0090ff;
  border-radius: 3px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.2);
  margin: 12px 8px 16px;
  overflow: hidden;
  min-height: 60px;
}

.txn-chip__stripe {
  flex: 0 0 6px;
  background: #ffb100;
}

.txn-chip__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  gap: 6px;
  min-width: 0;
}

.txn-chip__top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.txn-chip__merchant {
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
}

.txn-chip__amount {
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  flex: 0 0 auto;
}

.txn-chip__date {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.85);
}

.reconcile-form >>> {
  .reconcile-transaction-overlay {
    border-radius: 0;
    border: none;
    box-shadow: none;
    padding: 0;

    > p.small {
      display: none;
    }
    .col-md-12 {
      padding-left: 0;
      padding-right: 0;
    }

    .dawn-file-size {
      display: none;
    }

    textarea {
      width: 100%;
    }

    .file-uploads {
      display: block;
      .btn-primary {
        display: block;
        background-color: #007bff;
        color: #fff;
        padding-top: 4px;
        padding-bottom: 4px;
        font-size: 12px;
      }
    }
  }

  .overview-card {
    display: none;
  }

  // Force single-column layout inside the mobile shell. ReconcileTransaction's
  // middle-band uses Bootstrap `col-md-6` for a side-by-side "form on the left,
  // receipt image on the right" desktop layout. At viewports >= 768px (tablet
  // emulation, landscape phones) this splits the form to half-width and either
  // leaves the right half empty (no attachment) or shows the receipt image at
  // half-width — neither matches the SubmitReceipt/phone design. Override the
  // grid splits so the columns stack full-width regardless of viewport width.
  // `order: -1` on the image block pulls it above the form (chip → image →
  // form → coding), mirroring the Figma 533:287 / 092123 vertical flow.
  .middle-band {
    // !important: the shared component's own scoped `.middle-band { display:
    // block }` ties this deep rule on specificity, so the winner falls to
    // style-injection order (build-dependent). Forcing it keeps the column
    // stack — and with it the `order: -1` receipt-first layout — deterministic.
    display: flex !important;
    flex-direction: column !important;
  }
  .middle-band-left,
  .middle-band-right {
    width: 100% !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
    float: none !important;
  }
  .middle-band-right {
    order: -1;
  }

  // Mobile-only CAPS for the Receipt-section actions (Figma look). The shared
  // component's button labels are sentence case for desktop web (golden — must
  // not change); the mobile Additional Documents buttons are already literal
  // CAPS, so this rule is a visual no-op for them.
  .receipt-scan-actions .receipt-action-button {
    text-transform: uppercase;
  }

  .full-width-table {
    tbody, thead {
      float: left;
      width: 0;
    }
    // Small 8px breathing space between rows. The per-row split-buttons strip
    // sits inside each row's flex column via `order: 999` (see `.split-buttons`
    // rule below), in normal flow — so it doesn't overflow into the inter-row
    // gap and the row's `border-bottom: 1px solid #ddd` reads as a visible
    // delimiter between subtotal / tax / tip rows.
    tr + tr {
      margin-top: 8px !important;
    }
    tbody tr {
      width: 50%;
      // Flex column so we can `order: 999` the split-buttons cell to the
      // bottom of each row's vertical stack (see `.split-buttons` rule
      // below). With `display: block` the split-buttons would sit at the
      // TOP of the row (source order: first <td>). Flex + order keeps the
      // source order intact for desktop while visually placing the action
      // strip last on mobile.
      display: flex;
      flex-direction: column;
      position: relative;
      border-bottom: 1px solid #ddd;
      padding-bottom: 11px;
      width: 90vw;
    }
    td {
      display: block;
      width: 90vw;
      margin-bottom: 8px !important;
    }
    th {
      display: none;
      width: 50%;
      margin-bottom: 60px;
      position: relative;
      top: -40px;
      width: 100vw;
      padding-left: 5px;
    }
    td input.custom-fields {
      padding-left: 14px;
    }
    .form-group.money-input {
      margin-top: 2px;
    }
    td input.custom-fields::placeholder {
      color: #bcbcbc !important
    }
  }

  .split-buttons {
    // Per-row layout (pre-EPC behavior restored): every line-item row shows
    // its own `+ Split / Copy / Paste / Remove` strip so users can Copy/Paste
    // across rows and Remove any middle row, not just the last. `order: 999`
    // keeps the strip at the bottom of each row's flex column (tr is `display:
    // flex; flex-direction: column` above) — source order has split-buttons
    // first, so without `order` they'd render above the Amount field. Normal
    // flow (no `position: absolute`) — previous `bottom: -40px` overflowed
    // the row and was unreachable when scroll-height was tight.
    order: 999;
    padding-top: 8px !important;
  }
}

.card {
  border-radius: 6px;
  border-top-width: 0px !important;
  border-right-width: 0px !important;
  border-bottom-width: 0px !important;
  box-shadow: 1px 3px 5px 0px rgba(0,0,0,0.15);

  >>> &.no-icon .icon-wrapper.no-icon {
    min-width: 0;
  }
}

.summary {
  width: 100%;
}

.preview {
  .icon {
    color: #aaa;
    font-size: 64px;
    padding: 0.5rem 1rem 0.5rem 0;
    float: left;
  }
}

.optional-text {
  font-style: italic;
  opacity: 0.7;
}

.custom-button {
  background-color: #007bff;
  color: #fff;
  border: 1px solid #007bff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.custom-button1 {
  background-color: #fff;
  color: black;
  border: 1px solid black;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.custom-button:hover {
  background-color: #007bff;
  color: #fff;
}

.overflow-merchant {
  text-overflow: ellipsis;
  overflow: hidden;
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/EvedPay/transactiondetails.vue